import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Table,
  ResultsRow,
  ResultsHeader,
  PlainButton,
  ControlWrapper,
  SearchWrapper,
} from 'components/styled-components';
import Pagination from 'components/pagination';
import { fetchAllDepartments } from 'actions/utils';
import DepartmentsTable from 'containers/Manage-Admin/departments/table';
import colors from 'utils/colors';
import AddDepartmentDrawer from 'containers/Manage-Admin/departments/addDepartmentDrawer';
import shortid from 'shortid';
import hasPermission from 'utils/hasPermission';
import permissions from 'utils/permissions';
import { InputGroup, NonIdealState } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import FilterMenu from 'containers/Manage-Admin/departments/filterMenu';
import debounce from 'lodash/debounce';
import constants from 'utils/constants';
import Loading from 'components/loading';

const STATUS_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
];

const DepartmentsBase = ({
  fetchAllDepartments,
  departments,
  appUser,
  totalCount,
  isFetching,
}) => {
  const [isOpen, toggleOpen] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetch();
  }, [searchQuery, status, currentPage, perPage]);

  const fetch = () => {
    fetchAllDepartments({
      searchQuery,
      statusName: status.value,
      limit: perPage,
      start: currentPage * perPage,
    });
  };

  const handlePageClick = page => {
    setCurrentPage(page);
  };

  const handlePerPageChange = page => {
    setPerPage(page.value);
    setCurrentPage(0);
  };

  const throttledSearch = debounce(
    setSearchQuery,
    constants.SEARCH_TYPING_DELAY_MILLISECONDS
  );

  const handleQueryChange = e => {
    throttledSearch(e.target.value);
    setCurrentPage(0);
  };

  const handleChangeFilter = selected => {
    setStatus(selected);
    setCurrentPage(0);
  };

  return (
    <>
      <SearchWrapper padding="1rem 2rem">
        <ControlWrapper>
          <InputGroup
            leftIcon={IconNames.SEARCH}
            placeholder={`Search Departments`}
            className="medium-text-group light-blue"
            onChange={handleQueryChange}
          />
          <div style={{ marginRight: '1rem' }} />
          <FilterMenu
            handleChangeFilter={handleChangeFilter}
            selectedFilter={status}
            statusFilters={STATUS_OPTIONS}
          />
        </ControlWrapper>
        {hasPermission(appUser.permissions, permissions.CREATE_DEPARTMENTS) && (
          <PlainButton onClick={toggleOpen}>ADD DEPARTMENT</PlainButton>
        )}
      </SearchWrapper>

      <Table>
        <tbody>
          <ResultsRow
            background={colors.whiteGray}
            cursor="default"
            border={`1px solid ${colors.lightestGray}`}
          >
            <ResultsHeader>Department Name</ResultsHeader>
            <ResultsHeader>Department Description</ResultsHeader>
            <ResultsHeader>Department Status</ResultsHeader>
            <ResultsHeader>Actions</ResultsHeader>
          </ResultsRow>
          {isFetching && (
            <tr>
              <td colSpan={4}>
                <Loading size={100} />
              </td>
            </tr>
          )}
          {!!totalCount &&
            !isFetching &&
            departments.map(d => (
              <DepartmentsTable
                department={d}
                key={shortid.generate()}
                fetch={fetch}
                appUser={appUser}
                searchQuery={searchQuery}
              />
            ))}
          {!!!totalCount && !isFetching && (
            <tr>
              <td colSpan={4}>
                <NonIdealState
                  title="No departments found"
                  icon="error"
                  description="There were not any departments returned"
                />
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Pagination
        numRecords={totalCount}
        perPage={perPage}
        currentPage={currentPage}
        onPageClick={handlePageClick}
        handlePerPageChange={handlePerPageChange}
        hidePerPage={false}
      />
      <AddDepartmentDrawer
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        fetch={fetch}
      />
    </>
  );
};

const mapStateToProps = state => ({
  departments: state.utils.departments,
  totalCount: state.utils.departmentsTotal,
  isFetching: state.utils.isFetchingDepartments,
});

const Departments = connect(
  mapStateToProps,
  { fetchAllDepartments }
)(DepartmentsBase);

export default Departments;
